import React, { useMemo } from 'react';
import { MembershipDto } from '@area-restrita/api-client/model/membershipDto';
import { GetMembershipsParams } from '@area-restrita/api-client/model/getMembershipsParams';
import { useGetMembershipsInfinite } from '@area-restrita/api-client/endpoints/memberships/memberships.gen';

import Combobox, { ComboboxOption } from '@/components/Combobox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useDebouncedState } from '@/hooks/useDebounce';

const AvatarItem: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-2">
    <Avatar className="h-5 w-5 size-5 rounded">
      <AvatarImage
        src={`https://api.dicebear.com/9.x/initials/svg?seed=${encodeURIComponent(
          label,
        )}`}
        alt={label}
      />
      <AvatarFallback className="rounded-lg">{label}</AvatarFallback>
    </Avatar>
    <span className="truncate">{label}</span>
  </div>
);

const toComboboxOption = (membership: MembershipDto): ComboboxOption => ({
  label: membership.user.fullName ?? membership.user.email,
  value: membership.user.id,
});

interface IProps extends Omit<GetMembershipsParams, 'page' | 'limit'> {
  value?: ComboboxOption;
  onChange?: (option?: ComboboxOption) => void;
  className?: string;

  placeholder?: string;
  searchPlaceholder?: string;
}

const UserSelector: React.FC<IProps> = ({
  value,
  onChange,
  roles,
  status,
  rules,
  placeholder,
  searchPlaceholder,
  // className,
  // ...props
}) => {
  const [searchValue, setSearchValue, rawSearchValue] =
    useDebouncedState<string>('');

  const query = useGetMembershipsInfinite(
    { page: 1, limit: 100, roles, status, rules, fullName: searchValue },
    {
      query: {
        getNextPageParam: (lastPage, _, lastPageParam) =>
          lastPage.data.length > 0 ? lastPageParam + 1 : null,
      },
    },
  );

  const users = useMemo(
    () =>
      query.data?.pages.flatMap<ComboboxOption>((page) =>
        page.data.map(toComboboxOption),
      ) ?? [],
    [query.data],
  );

  return (
    <Combobox
      options={users}
      onChangeValue={onChange}
      value={value}
      placeholder={placeholder ?? 'Selecione um usuário'}
      emptyText="Nenhum usuário encontrado"
      searchPlaceholder={searchPlaceholder ?? 'Procure um usuário...'}
      isLoading={query.isFetching}
      renderOption={AvatarItem}
      showSearch
      searchValue={rawSearchValue}
      onChangeSearchValue={setSearchValue}
    />
  );
};

export default UserSelector;
